import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Heading, Skeleton } from '@indeed/ifl-components';
import { ArrowLeft, ArrowRight } from '@indeed/ifl-icons';
import { Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { StickyActionFooter } from '../../../components/StickyActionFooter/StickyActionFooter';
import DraftApplicationTabs from '../components/DraftApplicationTabs';
import { useFindReviewBeforeSubmitDraftQuery } from './__generated__/ReviewPage.generated';
import GettextFactory from '../../../util/GettextFactory';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import ApplicationTabContent from './ApplicationTabContent';
import { useDeleteAutoApplyApplicationDraft } from '../hooks/useDeleteAutoApplyApplicationDraft';
import { useDeleteDraftNavigation } from '../hooks/useDeleteDraftNavigation';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';

/**
 * Application Details Review Page
 * Displays a review of a single application draft with tabbed interface
 */
const ReviewPage: React.FC = () => {
    const gt = GettextFactory();
    const location = useLocation();
    const { draftId } = useParams<{ draftId: string }>();
    const { goForward, goBack, canGoBack, exitSubFlow, loading: flowLoading } = usePageFlow();

    // Progressive-automation review detour: the confirmation page opens each
    // draft's review at `/auto-apply/{draftId}/review/edit` via enterSubFlow.
    // In that case the footer shows a single "Update" CTA (instead of
    // Previous / Next) that exits the sub-flow back to the confirmation page.
    // Derived from the route so it behaves the same on cold start / deep link.
    const inReviewEditSubFlow = location.pathname.endsWith('/review/edit');
    const [searchParams, setSearchParams] = useSearchParams();
    const deleteAutoApplyApplicationDraft = useDeleteAutoApplyApplicationDraft();
    const eventLogger = useEventLogger();

    // Local state for preview generation (updated via callback from ApplicationTabContent)
    const [previewGenerating, setPreviewGenerating] = useState(true);

    // Delete-and-advance: fired by the JobInfoHeader trash button, awaits the delete then walks to
    // the next draft (questions-aware). Shared with QuestionsPage so both pages behave identically.
    const { onDelete: handleDeleteDraft, isDeleting } = useDeleteDraftNavigation();

    // Calculate derived values from query params (needed by callback)
    const draftIds = searchParams.get('draftIds')?.split(',').filter(Boolean) || [];
    const draftIdIndex = draftId ? draftIds.indexOf(draftId) : -1;

    // Get failed draft IDs from query params (GraphQL query failures)
    const failedDraftIdsParam = searchParams.get('failedDraftIds');
    const failedDraftIds = failedDraftIdsParam
        ? failedDraftIdsParam.split(',').filter(Boolean)
        : [];
    const isCurrentDraftFailed = draftId ? failedDraftIds.includes(draftId) : false;

    // Get preview failed draft IDs from query params (preview generation failures)
    const previewFailedDraftIdsParam = searchParams.get('previewFailedDraftIds');
    const previewFailedDraftIds = previewFailedDraftIdsParam
        ? previewFailedDraftIdsParam.split(',').filter(Boolean)
        : [];
    const isCurrentDraftPreviewFailed = draftId ? previewFailedDraftIds.includes(draftId) : false;

    // Callback to receive preview state updates from ApplicationTabContent
    const handlePreviewStateChange = useCallback(
        (isLoading: boolean, hasError: boolean, callbackDraftId: string | undefined) => {
            setPreviewGenerating(isLoading);

            // When preview generation completes with an error, add to failed list
            if (hasError && !isLoading && callbackDraftId) {
                setSearchParams(
                    (prev) => {
                        const newParams = new URLSearchParams(prev);
                        const currentPreviewFailed =
                            newParams.get('previewFailedDraftIds')?.split(',').filter(Boolean) ||
                            [];
                        if (!currentPreviewFailed.includes(callbackDraftId)) {
                            currentPreviewFailed.push(callbackDraftId);
                        }
                        newParams.set('previewFailedDraftIds', currentPreviewFailed.join(','));
                        return newParams;
                    },
                    { replace: true },
                );
            }
        },
        [setSearchParams],
    );

    const { data, loading, error } = useFindReviewBeforeSubmitDraftQuery({
        variables: {
            input: {
                applicationDraftId: draftId ?? '',
                skipSourceValidation: true,
            },
        },
        fetchPolicy: 'network-only',
        skip: draftIds.length === 0 || draftIdIndex < 0 || isCurrentDraftFailed || !draftId,
    });

    const draft = data?.findReviewBeforeSubmitDraft?.draft;

    // Add draft to failedDraftIds when query completes without draft
    // Also delete draft if failure is due to GraphQL error
    useEffect(() => {
        if (!loading && !draft && draftId && draftIdIndex >= 0 && !isCurrentDraftFailed) {
            // Only log here when there's no GraphQL/network error — Apollo's
            // error link already handles those cases. This covers the "successful
            // response but draft missing" scenario.
            if (!error) {
                eventLogger.logReviewError?.({
                    currentPage: location.pathname,
                    errorType: { string: 'draft-load-error' },
                    errorMessage: {
                        string: 'Draft not found in query response',
                    },
                    draftId: { string: draftId },
                    graphqlOperation: { string: 'findReviewBeforeSubmitDraft' },
                });
            }

            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev);
                    const currentFailed =
                        newParams.get('failedDraftIds')?.split(',').filter(Boolean) || [];
                    if (!currentFailed.includes(draftId)) {
                        currentFailed.push(draftId);
                    }
                    newParams.set('failedDraftIds', currentFailed.join(','));
                    return newParams;
                },
                { replace: true },
            );

            // Delete draft if failure is due to server error (not network/timeout). Best-effort
            // cleanup — swallow a failed delete (the hook now rejects on failure) since the draft is
            // already excluded from the batch via failedDraftIds.
            if (error?.graphQLErrors && error.graphQLErrors.length > 0) {
                void deleteAutoApplyApplicationDraft(draftId, null).catch(() => {});
            }
        }
    }, [
        loading,
        draft,
        draftId,
        draftIdIndex,
        isCurrentDraftFailed,
        error,
        setSearchParams,
        deleteAutoApplyApplicationDraft,
        eventLogger,
        location.pathname,
    ]);

    if (draftIds.length === 0) {
        return <Navigate to={`/auto-apply/confirmation${location.search}`} replace />;
    }

    if (draftIdIndex < 0) {
        return <Navigate to={`/auto-apply/${draftIds[0]}/review${location.search}`} replace />;
    }

    // Disable Previous/Next buttons when:
    // 1. Draft deletion is in progress, OR
    // 2. The flow's step list is still resolving — until the unfilled-question counts land, the
    //    next draft's questions step is missing from the spine and Next would skip past it, OR
    // 3. Draft/preview is loading (unless it failed - then allow navigation away as escape hatch)
    const shouldDisableButtons =
        isDeleting ||
        flowLoading ||
        (!isCurrentDraftFailed && !isCurrentDraftPreviewFailed && (!draft || previewGenerating));

    // Footer component used across all states
    const footer = (
        <StickyActionFooter>
            {inReviewEditSubFlow ? (
                // Review detour (progressive automation): single Update CTA that
                // exits the sub-flow back to the confirmation page.
                <Button
                    variant="primary"
                    size="lg"
                    onClick={() => {
                        eventLogger.logReviewButtonClick?.({
                            currentPage: location.pathname,
                            button: 'update',
                            draftId: AvroOptional.string(draftId),
                        });
                        void exitSubFlow('/auto-apply/confirmation');
                    }}
                    sx={{ flex: 1 }}
                    disabled={isDeleting}
                >
                    {gt.gettext('Update')}
                </Button>
            ) : (
                <>
                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={() => {
                            eventLogger.logReviewButtonClick?.({
                                currentPage: location.pathname,
                                button: 'previous',
                                draftId: AvroOptional.string(draftId),
                            });
                            void goBack();
                        }}
                        sx={{ flex: 1 }}
                        iconBefore={<ArrowLeft aria-hidden />}
                        disabled={shouldDisableButtons || !canGoBack}
                    >
                        {gt.gettext('Previous')}
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={() => {
                            eventLogger.logReviewButtonClick?.({
                                currentPage: location.pathname,
                                button: 'next',
                                draftId: AvroOptional.string(draftId),
                            });
                            void goForward();
                        }}
                        sx={{ flex: 1 }}
                        iconAfter={<ArrowRight aria-hidden />}
                        disabled={shouldDisableButtons}
                    >
                        {gt.gettext('Next')}
                    </Button>
                </>
            )}
        </StickyActionFooter>
    );

    if (isCurrentDraftFailed) {
        return (
            <>
                <PageTransition pageKey={`preview-${draftId}`}>
                    <Helmet>
                        <title>{gt.gettext('Unable to Load Application - Indeed')}</title>
                    </Helmet>

                    {/* Main content container. Uses 100vh (not 100%) so the
                        error state centers within the full viewport: PageTransition
                        is a plain block wrapper, so a percentage height here has no
                        definite containing block to resolve against and would
                        collapse to the content height (top-aligned). The loaded
                        state below can use 100% because its content is always tall
                        enough to fill the viewport on its own. (IAFE-2526) */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minBlockSize: '100vh',
                            maxInlineSize: '450px',
                            margin: '0 auto',
                            backgroundColor: 'background',
                            paddingBlockEnd: 10,
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingInline: 4,
                            textAlign: 'center',
                        }}
                    >
                        <Alert9 size="md" aria-hidden sx={{ marginBlockEnd: 4 }} />
                        <Heading level={3}>{gt.gettext("We can't load this job")}</Heading>
                    </Box>
                </PageTransition>
                {footer}
            </>
        );
    }

    if (!draft || loading) {
        return (
            <>
                <PageTransition pageKey={`preview-${draftId}`}>
                    <Helmet>
                        <title>{gt.gettext('Loading Application Details - Indeed')}</title>
                    </Helmet>

                    {/* Main content container. Uses 100vh (not 100%) so the loading
                        skeleton fills the viewport while data loads — see the note on
                        the failed state above for why a percentage height collapses
                        through PageTransition. (IAFE-2526) */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minBlockSize: '100vh',
                            maxInlineSize: '450px',
                            margin: '0 auto',
                            backgroundColor: 'background',
                            paddingBlockEnd: 10,
                        }}
                    >
                        {/* Job Info Header Skeleton */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                padding: 4,
                                gap: 4,
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Skeleton
                                    variant="text"
                                    sx={{ inlineSize: '80%', marginBlockEnd: 2 }}
                                />
                                <Skeleton variant="text" sx={{ inlineSize: '60%' }} />
                            </Box>
                            <Skeleton
                                variant="square"
                                sx={{ inlineSize: '24px', blockSize: '24px' }}
                            />
                        </Box>

                        {/* Tabs skeleton */}
                        <Box
                            sx={{
                                paddingBlockStart: 0,
                                paddingBlockEnd: 0,
                                paddingInlineStart: 4,
                                paddingInlineEnd: 4,
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 5, marginBlockEnd: 3 }}>
                                <Skeleton variant="text" sx={{ inlineSize: '80px' }} />
                                <Skeleton variant="text" sx={{ inlineSize: '80px' }} />
                            </Box>
                        </Box>

                        {/* Content skeleton - matching Figma design */}
                        <Box sx={{ padding: 4, flex: 1 }}>
                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '50%', marginBlockEnd: 3 }}
                            />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '70%', marginBlockEnd: 3 }}
                            />
                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '40%', marginBlockEnd: 4 }}
                            />

                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '50%', marginBlockEnd: 3 }}
                            />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '70%', marginBlockEnd: 3 }}
                            />
                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '40%', marginBlockEnd: 4 }}
                            />

                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '50%', marginBlockEnd: 3 }}
                            />
                            <Skeleton
                                variant="text"
                                sx={{ inlineSize: '70%', marginBlockEnd: 3 }}
                            />
                            <Skeleton variant="text" sx={{ marginBlockEnd: 3 }} />
                            <Skeleton variant="text" sx={{ inlineSize: '40%' }} />
                        </Box>
                    </Box>
                </PageTransition>
                {footer}
            </>
        );
    }

    const { jobTitle } = draft;

    return (
        <>
            <PageTransition pageKey={`preview-${draftId}`}>
                <Helmet>
                    <title>{gt.gettext('Application Details - {0}', jobTitle)}</title>
                    <meta
                        name="description"
                        content={gt.gettext('Review your application details')}
                    />
                </Helmet>

                {/* The tabbed content region (sticky JobInfoHeader + TabList, Application and
                    Job details tabs) is shared with QuestionsPage via DraftApplicationTabs. */}
                <DraftApplicationTabs
                    draft={draft}
                    onDelete={handleDeleteDraft}
                    isDeleting={isDeleting}
                >
                    <ApplicationTabContent
                        fragment={draft}
                        onPreviewStateChange={handlePreviewStateChange}
                    />
                </DraftApplicationTabs>
            </PageTransition>
            {footer}
        </>
    );
};

export default ReviewPage;
